import { get } from "lodash";
import React, { Component, Fragment } from "react";
import { connect } from "react-redux";
import { PropTypes } from "prop-types";
import {
  Button,
  Container,
  Divider,
  Header,
  Loader,
  Segment
} from "semantic-ui-react";

import UnauthorisedFeature from "../../components/auth/UnauthorisedFeature.jsx";
import ActionDropdownMenu from "../../components/actionDropdownMenu/ActionDropdownMenu.jsx";
import MarkingMessage from "../../components/marking/MarkingMessage.jsx";
import UserDetailsPanel from "../../components/users/UserDetailsPanel.jsx";
import Breadcrumbs from "../../components/breadcrumbs/Breadcrumbs.jsx";
import ResourceMarker from "../../components/marking/ResourceMarker.jsx";
import EntityUserMembershipList from "../../components/users/EntityUserMembershipList.jsx";
import SegmentMessage from "../../components/forms/messages/SegmentMessage.jsx";
import LinkWithReset from "../../components/utils/LinkWithReset.jsx";

import withConfirmationModal from "../../components/modals/withConfirmationModal.js";

import { AT_TYPES, RESOURCE_URI_CHUNKS } from "../../constants/entity.js";
import { PROJECTS } from "../../state/actions/actionConstants.js";
import { OPERATIONS } from "../../constants/operations.js";
import { ROUTES } from "../../constants/routing.js";

import { getDisabledStateFromMarking } from "../../utils/marking.js";

import { getEntityAction } from "../../state/actions/entityActions.js";
import { operationAction } from "../../state/reducers/operations/operationActions.js";

import entitySelector from "../../state/reducers/entity/entitySelector.js";
import endpointSelector from "../../state/reducers/endpoints/endpointSelector.js";
import userSelector from "../../state/reducers/users/userSelector.js";
import { selectCopy } from "../../state/reducers/locale/copySelector.js";

class EntityUser extends Component {
  static propTypes = {
    entity: PropTypes.object,
    user: PropTypes.object,
    userIsEntityOwner: PropTypes.bool.isRequired,
    isFetching: PropTypes.bool.isRequired,
    listProjectAvailability: PropTypes.object.isRequired,
    copyKey: PropTypes.string.isRequired,
    copy: PropTypes.func.isRequired,

    getEntityAction: PropTypes.func.isRequired,
    operationAction: PropTypes.func.isRequired
  };

  static defaultProps = {
    copyKey: "project_user"
  };

  constructor(props) {
    super(props);

    this.state = {
      entityId: `/${RESOURCE_URI_CHUNKS.PROJECTS}/${props.match.params.entityid}`,
      userId: `/${RESOURCE_URI_CHUNKS.USERS}/${props.match.params.id}`,
      markingData: {},
      marking: null,

      modalCopyKey: "",
      modalData: {},
      modalAction: null
    };
  }

  static getDerivedStateFromProps(props, state) {
    const marking = get(props, "entity.marking", null);

    if (marking !== state.marking) {
      const markingData = getDisabledStateFromMarking(marking);
      return { marking, markingData };
    }

    return null;
  }

  componentDidMount = () => {
    const { getEntityAction, listProjectAvailability, location } = this.props;

    if (listProjectAvailability.available) {
      getEntityAction(this.state.entityId, { showErrorModal: false });
      getEntityAction(location.pathname, { showErrorModal: false });
    }
  };

  // -- HANDLERS & ACTIONS -- //

  doEdit = values => {
    const { user, operationAction } = this.props;
    operationAction(user, OPERATIONS.UPDATE, values);
  };

  // -- MODAL -- //

  openModal = () => {
    const { user, openModal } = this.props;
    const { modalCopyKey, modalAction } = this.state;

    openModal(
      modalAction,
      modalCopyKey,
      undefined,
      user,
      this.renderResourceList(modalCopyKey)
    );
  };

  // -- RENDER -- //

  getFullName = () => {
    const { user } = this.props;

    if (!user) return "";

    return `${user.firstName} ${user.lastName}`;
  };

  getBreadcrumbSections = () => {
    const { copy, entity } = this.props;
    const { entityId } = this.state;

    return [
      { content: copy("navigation.projects"), href: ROUTES.PROJECTS }, // TODO - generalise this to be based on entity @type
      {
        content: get(entity, "name", "Unknown"),
        href: entityId
      },
      {
        content: copy("navigation.project_users"),
        href: `${entityId}/${RESOURCE_URI_CHUNKS.USERS}`
      },
      {
        content: this.getFullName(),
        active: true
      }
    ];
  };

  renderMembershipList = (memberships, listCopyKey) => {
    const { location, copy } = this.props;

    const fullName = this.getFullName();

    if (!memberships || memberships.length < 1) {
      return (
        <p>{`${fullName} ${copy(`${listCopyKey}.no_access_description`)}`}</p>
      );
    }

    return (
      <EntityUserMembershipList
        entityId={this.state.entityId}
        memberships={memberships}
        location={location}
        copyKey={listCopyKey}
      />
    );
  };

  renderIsOwnerMessage = () => {
    const { copyKey, copy } = this.props;

    const fullName = this.getFullName();

    return (
      <SegmentMessage
        title={`${fullName} ${copy(`${copyKey}.is_owner.title`)}`}
        message={copy(`${copyKey}.is_owner.message`)}
        info={true}
      />
    );
  };

  renderNoUserMessage = () => {
    const { entityId, userId } = this.state;
    const { entity, copyKey, copy } = this.props;

    const message = (
      <Fragment>
        {copy(`${copyKey}.not_available.message_1`)} <strong>{userId}</strong>{" "}
        {copy(`${copyKey}.not_available.message_2`)}{" "}
        <strong>
          <LinkWithReset to={entityId}>
            {get(entity, "name", "Unknown")}
          </LinkWithReset>
        </strong>
      </Fragment>
    );

    return (
      <Fragment>
        <SegmentMessage
          title={copy(`${copyKey}.not_available.title`)}
          message={message}
          info={true}
        />
        <Button
          as={LinkWithReset}
          to={`${entityId}/${RESOURCE_URI_CHUNKS.USERS}`}
          className="float-right"
        >
          {copy(`${copyKey}.not_available.view_users_cta`)}
        </Button>
      </Fragment>
    );
  };

  renderHeaderSet = () => {
    const { markingData } = this.state;
    const { entity, copy } = this.props;

    return (
      <Fragment>
        {entity && (
          <Fragment>
            <Breadcrumbs sections={this.getBreadcrumbSections()} />

            <ActionDropdownMenu
              data={entity}
              copy={copy("project_dropdown_menu")}
            />

            <Header as="h3" className="action-dropdown-header">
              {get(entity, "name", "Unknown")} User
            </Header>

            <ResourceMarker
              marking={entity.marking}
              className="project-marker clearfix"
            />

            <MarkingMessage
              type={MarkingMessage.USER}
              message={markingData.message}
            />
          </Fragment>
        )}
        <Divider className="clear-both" />
      </Fragment>
    );
  };

  render() {
    const {
      user,
      isFetching,
      listProjectAvailability: { permitted, enabled },
      userIsEntityOwner,
      copyKey,
      copy
    } = this.props;

    if (!permitted) {
      return <UnauthorisedFeature title={copy("projects.title")} />;
    }

    // early return if we aren't ready to render
    if (isFetching) {
      return (
        <Container>
          {this.renderHeaderSet()}
          <Loader active inline="centered" />
        </Container>
      );
    }

    // early return if user isn't availabe
    if (!enabled || !user || !user["@id"]) {
      return (
        <Container>
          {this.renderHeaderSet()}
          {this.renderNoUserMessage()}
        </Container>
      );
    }

    return (
      <Container>
        {this.renderHeaderSet()}

        <UserDetailsPanel data={user} doEdit={this.doEdit} disabled={false} />

        {userIsEntityOwner && this.renderIsOwnerMessage()}

        <Segment>
          <Header as="h3">
            {copy(`${copyKey}.access_lists.groups.title`)}
          </Header>
          {this.renderMembershipList(
            user.groups,
            `${copyKey}.access_lists.groups`
          )}
        </Segment>
        <Segment>
          <Header as="h3">
            {copy(`${copyKey}.access_lists.resources.title`)}
          </Header>
          {this.renderMembershipList(
            user.workstations,
            `${copyKey}.access_lists.resources`
          )}
        </Segment>
      </Container>
    );
  }
}

const mapStateToProps = (state, props) => ({
  entity: entitySelector.selectEntity()(state, props),
  user: userSelector.selectUser(state, props),
  userIsEntityOwner: entitySelector.selectEntityUserIsEntityOwner()(
    state,
    props
  ),
  isFetching: state.ephemeralEntities.isFetching,
  listProjectAvailability: endpointSelector.selectOperationAvailability(state)(
    AT_TYPES.PROJECT,
    OPERATIONS.LIST
  ),
  copy: selectCopy(state)
});

const mapDispatchToProps = dispatch => ({
  getEntityAction: (id, options, dependencies) =>
    dispatch(getEntityAction(id, options, dependencies)),
  operationAction: (entity, operationName, data, options) =>
    dispatch(operationAction(PROJECTS, entity, operationName, data, options))
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withConfirmationModal(EntityUser));
